/**
 * Saved prompts — the person's own library, live (2026-09-17).
 *
 * ONE collection, `users/{uid}/prompt_templates/{promptId}`, owner-readable and
 * owner-writable under the recursive owner-uid grant that `firestore.rules`
 * already gives every `users/{uid}` subtree. No rules change was needed and
 * none should be added: `prompt_templates` is deliberately absent from the
 * per-segment exclusion lists, and putting it there — by pattern-matching
 * `domain_memory` or `wallet`, which are server-written for reasons that do not
 * apply here — would silently break the whole feature.
 *
 * WRITES ARE CLIENT-DIRECT, on purpose. This is the `threadCategoriesRepository`
 * posture: a person arranging their own things needs no authorization ladder,
 * and routing it through a callable would cost a cold start and a round trip on
 * every save while destroying the optimistic list. The memory plane's
 * single-writer discipline is not a counter-example — a memory row carries a
 * content hash, a supersession chain and an append-only event inside one
 * transaction, and a prompt carries none of that. What IS shared, and must stay
 * shared, is the VALIDATOR: every write here goes through `validatePrompt` in
 * `types/savedPrompts`, so the shapes this writes are the shapes everything
 * else reads.
 *
 * The paths come from `Paths.promptTemplatesCol` / `promptTemplateDoc`, which
 * are written as slash paths rather than segments because the data-rights
 * scanner reads that form — see the note beside them in `firestoreUtils`.
 */
import {
  deleteDoc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import db from '../utils/firestore';
import { Paths, assertCurrentUid, withBackoff } from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import { foldCategoryName } from '../types/categoryNames';
import {
  MAX_PROMPTS_PER_PERSON,
  SAVED_PROMPT_SCHEMA_VERSION,
  canAddPrompt,
  newPromptId,
  normalizePromptCategory,
  orderPrompts,
  parseSavedPrompt,
  validatePrompt,
  type SavedPrompt,
  type SavedPromptDraft,
  type Verdict,
} from '../types/savedPrompts';

/**
 * Live list of the viewer's prompts, ordered the way the picker shows them.
 *
 * A malformed row is dropped rather than half-rendered, and an error — offline,
 * a rules refusal — is SWALLOWED: the surface keeps its last known list instead
 * of blanking, which is the one thing a picker must never do to somebody
 * halfway through composing.
 */
export function subscribeSavedPrompts(
  uid: string,
  onChange: (prompts: SavedPrompt[]) => void,
  onError?: () => void,
): Unsubscribe {
  assertCurrentUid(uid, 'subscribeSavedPrompts');
  return onSnapshot(
    Paths.promptTemplatesCol(uid),
    (snap) => {
      const rows: SavedPrompt[] = [];
      snap.forEach((docSnap) => {
        const parsed = parseSavedPrompt(docSnap.id, docSnap.data());
        if (parsed) rows.push(parsed);
      });
      onChange(orderPrompts(rows));
    },
    () => { onError?.(); },
  );
}

/**
 * Write a new prompt. `count` is the caller's live list length — counting needs
 * the list, and the caller is already holding it.
 */
export async function createSavedPrompt(
  uid: string,
  draft: SavedPromptDraft,
  count: number,
): Promise<Verdict<SavedPrompt>> {
  addUserScopedWriteGuard(uid, 'createSavedPrompt');
  assertCurrentUid(uid, 'createSavedPrompt');
  const room = canAddPrompt(count, MAX_PROMPTS_PER_PERSON);
  if (!room.ok) return room;
  const verdict = validatePrompt(draft);
  if (!verdict.ok) return verdict;
  const now = Date.now();
  const prompt: SavedPrompt = {
    id: newPromptId(now),
    ...verdict.value,
    createdAt: now,
    updatedAt: now,
    createdBy: uid,
    schemaVersion: SAVED_PROMPT_SCHEMA_VERSION,
  };
  await withBackoff(() => setDoc(Paths.promptTemplateDoc(uid, prompt.id), prompt));
  return { ok: true, value: prompt };
}

/** Change one. Returns the normalized result so the caller can settle its optimistic row. */
export async function updateSavedPrompt(
  uid: string,
  id: string,
  draft: SavedPromptDraft,
): Promise<Verdict<Omit<SavedPrompt, 'id' | 'createdAt' | 'createdBy'>>> {
  addUserScopedWriteGuard(uid, 'updateSavedPrompt');
  assertCurrentUid(uid, 'updateSavedPrompt');
  const verdict = validatePrompt(draft);
  if (!verdict.ok) return verdict;
  const patch = { ...verdict.value, updatedAt: Date.now() };
  await withBackoff(() => updateDoc(Paths.promptTemplateDoc(uid, id), patch));
  return { ok: true, value: patch };
}

export async function deleteSavedPrompt(uid: string, id: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'deleteSavedPrompt');
  assertCurrentUid(uid, 'deleteSavedPrompt');
  await withBackoff(() => deleteDoc(Paths.promptTemplateDoc(uid, id)));
}

/**
 * Stamp that a prompt was just used — what the picker's default order reads, so
 * the two or three somebody actually runs rise without being pinned by hand.
 *
 * Deliberately fire-and-forget: a failed stamp costs an ordering nicety, and
 * making somebody wait on it before their text reaches the draft would be the
 * wrong trade every time.
 */
export function markSavedPromptUsed(uid: string, id: string, useCount: number): void {
  try {
    addUserScopedWriteGuard(uid, 'markSavedPromptUsed');
    assertCurrentUid(uid, 'markSavedPromptUsed');
  } catch {
    return;
  }
  void updateDoc(Paths.promptTemplateDoc(uid, id), {
    lastUsedAt: Date.now(),
    useCount: (Number.isFinite(useCount) ? useCount : 0) + 1,
  }).catch(() => undefined);
}

/**
 * Rename a category across every prompt filed under it.
 *
 * The category list is DERIVED from the prompts rather than kept in a second
 * document, so a rename is this batch and nothing else — there is no registry
 * to update, nothing to leave dangling, and no way for the two to disagree.
 * One batch, so it is all-or-nothing; `MAX_PROMPTS_PER_PERSON` is well inside
 * Firestore's 500-operation limit, which is the other reason that cap exists.
 *
 * Matching folds case and accents, exactly as thread sections do, so renaming
 * "clients" also moves everything filed under "Clients".
 */
export async function renamePromptCategory(
  uid: string,
  from: string,
  to: string,
): Promise<number> {
  addUserScopedWriteGuard(uid, 'renamePromptCategory');
  assertCurrentUid(uid, 'renamePromptCategory');
  const fromKey = foldCategoryName(from);
  const next = normalizePromptCategory(to);
  if (!fromKey) return 0;
  const snap = await withBackoff(() => getDocs(Paths.promptTemplatesCol(uid)));
  const batch = writeBatch(db);
  let touched = 0;
  snap.forEach((docSnap) => {
    const data = docSnap.data() as { category?: unknown };
    if (foldCategoryName(data?.category) !== fromKey) return;
    batch.update(docSnap.ref, { category: next, updatedAt: Date.now() });
    touched += 1;
  });
  if (touched) await withBackoff(() => batch.commit());
  return touched;
}
